import React, { Suspense } from "react";
import { Button, tokens } from "@fluentui/react-components";

// The Markdown toolchain is ~155 KiB and is never needed before the assistant's
// first answer — which is always at least one LLM round-trip away — so it loads
// in its own chunk while the bubble shows escaped plain text (CLAUDE.md A10).
// Must stay a lazy import: a static one would pull the stack back into the
// initial bundle.
const MarkdownContent = React.lazy(() => import("./MarkdownContent"));

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  isTyping?: boolean;
  isToolStatus?: boolean;
  /** When set, renders an "Insert into email" action under the bubble — used
   * in compose mode to push this message's text into the draft body. */
  onInsert?: () => void;
}

/**
 * Reduces Markdown to plain text for insertion into the compose body via
 * `Office.CoercionType.Text` (see `insertReplyBody` in office-mail.ts) —
 * without this, literal `**`/`#`/`-`/`[]()` markers would show up in the
 * draft.
 */
export function stripMarkdownToPlainText(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*\*|___)(.+?)\1/g, "$2")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(\*|_)(.+?)\1/g, "$2")
    .replace(/^[ \t]*[-*+]\s+/gm, "")
    .replace(/^[ \t]*\d+\.\s+/gm, "");
}

/**
 * Picks out the text to insert into the compose draft from an assistant
 * message. In compose mode the system prompt (see `renderComposeModeBlock`
 * in agent-loop.ts) instructs the model to put a complete, ready-to-use
 * draft — and nothing else — inside a single fenced code block, so the rest
 * of the message (commentary, a list of what changed, etc.) is never
 * inserted. Falls back to the whole message, plain-texted, for replies that
 * don't follow that convention (e.g. free-form chat with no code block).
 */
export function extractEmailBodyForInsert(markdown: string): string {
  const codeBlock = markdown.match(/```[a-zA-Z]*\n?([\s\S]*?)```/);
  return stripMarkdownToPlainText((codeBlock ? codeBlock[1] : markdown).trim());
}

export function ChatMessage({ role, content, isTyping, isToolStatus, onInsert }: ChatMessageProps) {
  const isUser = role === "user";

  const bubbleStyle: React.CSSProperties = {
    maxWidth: "85%",
    padding: "8px 12px",
    borderRadius: 12,
    wordBreak: "break-word",
    backgroundColor: isUser
      ? tokens.colorBrandBackground
      : isToolStatus
        ? "transparent"
        : tokens.colorNeutralBackground3,
    color: isUser ? tokens.colorNeutralForegroundOnBrand : tokens.colorNeutralForeground1,
    alignSelf: isUser ? "flex-end" : "flex-start",
    fontStyle: isToolStatus ? "italic" : "normal",
    fontSize: isToolStatus ? 12 : 14,
  };

  if (isTyping) {
    return (
      <div style={{ alignSelf: "flex-start" }}>
        <div data-testid="typing-indicator" style={{ ...bubbleStyle, display: "flex", gap: 4, alignItems: "center" }}>
          {[0, 1, 2].map(i => (
            <span
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: tokens.colorNeutralForeground3,
                display: "inline-block",
                animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Tool status lines and user input stay as plain, pre-wrapped text — only the
  // assistant's final answer is rendered as sanitized Markdown.
  if (isToolStatus || isUser) {
    return (
      <div style={{ alignSelf: isUser ? "flex-end" : "flex-start" }}>
        <div style={{ ...bubbleStyle, whiteSpace: "pre-wrap" }}>{content}</div>
      </div>
    );
  }

  return (
    <div style={{ alignSelf: "flex-start" }}>
      <div style={bubbleStyle}>
        <Suspense fallback={<div style={{ whiteSpace: "pre-wrap" }}>{content}</div>}>
          <MarkdownContent content={content} />
        </Suspense>
      </div>
      {onInsert && (
        <Button size="small" appearance="subtle" onClick={onInsert} style={{ marginTop: 4 }}>
          Insert into email
        </Button>
      )}
    </div>
  );
}
